import React, { useState, useCallback } from 'react'
import type { JSX } from 'react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAppAuth'
import { logger } from '@/utils/logger'
import type { ButtonProps } from '@/components/ui/button'

/**
 * Props for LoginButton component
 */
interface LoginButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** Custom button text (default: "Sign In") */
  children?: React.ReactNode
  /** Text to display during loading state */
  loadingText?: string
  /** Whether to show error messages inline */
  showErrors?: boolean
  /** Callback fired when sign-in process starts */
  onSignInStart?: () => void
  /** Callback fired when sign-in completes successfully */
  onSignInComplete?: () => void
  /** Callback fired when sign-in fails */
  onSignInError?: (error: Error) => void
  /** Whether to hide the button when user is authenticated */
  hideWhenAuthenticated?: boolean
}

/**
 * Login button component that initiates the authentication flow
 * 
 * Features:
 * - Initiates sign-in through auth service
 * - Loading states with accessible feedback
 * - Error handling and display
 * - Keyboard accessibility support
 * - Customizable appearance and behavior
 * - Automatically hides when user is authenticated
 * 
 * @example Basic usage
 * ```tsx
 * <LoginButton />
 * ```
 * 
 * @example Custom text and styling
 * ```tsx
 * <LoginButton variant="outline" size="lg">
 *   Log In Now
 * </LoginButton>
 * ```
 * 
 * @example With callbacks
 * ```tsx
 * <LoginButton 
 *   onSignInStart={() => analytics.track('login_started')}
 *   onSignInComplete={() => redirect('/dashboard')}
 *   onSignInError={(error) => showNotification(error.message)}
 * />
 * ```
 */
export const LoginButton = ({
  children = 'Sign In',
  loadingText = 'Signing In...',
  showErrors = true,
  onSignInStart,
  onSignInComplete,
  onSignInError,
  hideWhenAuthenticated = true,
  disabled,
  className,
  ...buttonProps
}: LoginButtonProps): JSX.Element | null => {
  const { signinRedirect, isLoading: authLoading, isAuthenticated, error: authError } = useAuth()
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  // Using unified hook with react-oidc-context methods

  /**
   * Handle sign-in button click
   */
  const handleSignIn = useCallback(async (): Promise<void> => {
    try {
      // Clear any previous errors
      setLocalError(null)
      
      // Set loading state
      setIsSigningIn(true)
      
      // Notify callback
      onSignInStart?.()
      
      // Initiate sign-in redirect through unified hook (uses react-oidc-context)
      await signinRedirect()
      
      // Note: signinRedirect() initiates a redirect, so onSignInComplete typically won't be called
      // as the user will be navigated away from this page. However, we call it in case
      // the redirect fails or is cancelled.
      onSignInComplete?.()
      
    } catch (error) {
      // Handle any errors from the sign-in redirect (store locally for display)
      const errorMessage = error instanceof Error ? error.message : 'Sign-in failed'
      setLocalError(errorMessage)
      
      // Notify error callback
      if (error instanceof Error) {
        onSignInError?.(error)
      } else {
        onSignInError?.(new Error('Sign-in failed'))
      }
      
      logger.error('Sign-in failed', error instanceof Error ? error : undefined)
      
    } finally {
      // Clear loading state
      setIsSigningIn(false)
    }
  }, [signinRedirect, onSignInStart, onSignInComplete, onSignInError])

  /**
   * Handle keyboard interactions
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>): void => {
    // Handle Enter and Space keys for accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleSignIn()
    }
  }, [handleSignIn])

  // Determine loading state (either local loading or auth context loading)
  const isLoading = isSigningIn || authLoading
  
  // Display local error (from redirect failures) or auth context error
  const displayError = localError || (authError ? authError.message : null)
  
  // Determine if button should be disabled
  const isDisabled = disabled || isLoading

  // Don't render if user is already authenticated (unless explicitly configured)
  if (hideWhenAuthenticated && isAuthenticated) {
    return null
  }

  // Always render the core component - let it handle its own loading states

  return (
    <div className="space-y-2">
      <Button
        onClick={handleSignIn}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-label={isLoading ? `${loadingText} Please wait` : `${children} - Opens authentication dialog`}
        className={className}
        {...buttonProps}
      >
        {isLoading ? loadingText : children}
      </Button>
      
      {/* Error message display */}
      {showErrors && displayError && (
        <div 
          className="text-sm text-destructive" 
          role="alert" 
          aria-live="assertive"
          data-testid="login-error"
        >
          {displayError}
        </div>
      )}
    </div>
  )
}

/**
 * Compact login button without error display
 * Useful for navigation bars or inline usage
 */
export const LoginButtonCompact = (props: Omit<LoginButtonProps, 'showErrors'>): JSX.Element | null => {
  return <LoginButton {...props} showErrors={false} />
}

/**
 * Login button with outline variant and custom styling
 * Pre-configured for common use cases
 */
export const LoginButtonOutline = (props: LoginButtonProps): JSX.Element | null => {
  return <LoginButton variant="outline" {...props} />
}

/**
 * Small login button for tight spaces
 */
export const LoginButtonSmall = (props: LoginButtonProps): JSX.Element | null => {
  return <LoginButton size="sm" {...props} />
}

/**
 * Large login button for landing pages or prominent placement
 */
export const LoginButtonLarge = (props: LoginButtonProps): JSX.Element | null => {
  return <LoginButton size="lg" {...props} />
}

/**
 * Hook for programmatic login triggering
 * Useful when you need to trigger login from non-button elements
 * 
 * @returns Object with login function and state
 * 
 * @example
 * ```tsx
 * const { triggerLogin, isLoading, error } = useLogin({
 *   onComplete: () => navigate('/dashboard')
 * })
 * 
 * const handleCardClick = () => {
 *   triggerLogin()
 * }
 * ```
 */
export const useLogin = (options?: {
  onStart?: () => void
  onComplete?: () => void
  onError?: (error: Error) => void
}): {
  triggerLogin: () => Promise<void>
  isLoading: boolean
  error: string | null
} => {
  const { signinRedirect, error: authError } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  // Using unified hook with react-oidc-context methods

  const triggerLogin = useCallback(async (): Promise<void> => {
    try {
      setLocalError(null)
      setIsLoading(true)
      
      options?.onStart?.()
      
      await signinRedirect()
      
      options?.onComplete?.()
      
    } catch (err) {
      // Handle any errors from the sign-in redirect (store locally)
      const errorMessage = err instanceof Error ? err.message : 'Sign-in failed'
      setLocalError(errorMessage)
      
      if (err instanceof Error) {
        options?.onError?.(err)
      } else {
        options?.onError?.(new Error('Sign-in failed'))
      }
      
    } finally {
      setIsLoading(false)
    }
  }, [signinRedirect, options])

  return {
    triggerLogin,
    isLoading,
    error: localError || (authError ? authError.message : null), // Local error takes precedence
  }
}
